import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Table from '../../../../../components/Table';
import rbacCheck from '../../../../../rbac/rbacCheck';
import {
  createMachineAccount,
  fetchMachineAccounts,
} from '../../../../../redux/safescarf/machineAccounts/actions';
import { updateUser } from '../../../../../redux/safescarf/users/actions';

import MachineAccountsApiKeyModal from './MachineAccountsApiKeyModal';

import { useUserData } from '@/redux/selectors';
import type { RootState } from '@/redux/store';
import type { TableField, TableRowData } from '@/types/tableTypes';

const MachineAccounts: React.FC = () => {
  const dispatch = useDispatch();
  const userData = useUserData();
  const machineAccounts = useSelector((state: RootState) => state.safescarf.machineAccounts);

  useEffect(() => {
    if (!machineAccounts.length) {
      dispatch(fetchMachineAccounts());
    }
  }, [dispatch, machineAccounts.length]);

  const machineAccountToken =
    machineAccounts.length > 0 ? machineAccounts[machineAccounts.length - 1]?.token : null;
  const machineAccountName =
    machineAccounts.length > 0 ? machineAccounts[machineAccounts.length - 1]?.username : '';

  const fields: TableField[] = [
    { id: 'username', title: 'Username' },
    { id: 'createdBy', title: 'Created by' },
    { id: 'status', title: 'Status', type: 'options' },
  ];

  const rows: TableRowData[] = machineAccounts
    .filter(ma => {
      const canViewMachineAccount = rbacCheck(
        userData.roles,
        '/products/safescarf/machine-accounts:VIEW',
        { createdBy: ma.email, userEmail: userData.email }
      );
      return canViewMachineAccount;
    })
    .map(ma => {
      const canUpdateMachineAccount = rbacCheck(userData.roles, '/products/safescarf/users:PUT', {
        userEmail: userData.email,
        rowEmail: ma.email,
      });
      return {
        key: ma.username,
        data: [
          { field: 'username', value: ma.username },
          { field: 'createdBy', value: ma.email },
          {
            field: 'status',
            type: 'switch',
            value: ma.is_active ? 'Active' : 'Inactive',
            props: {
              disabled: !canUpdateMachineAccount,
              checked: ma.is_active,
              label: ma.is_active ? 'Active' : 'Inactive',
              name: ma.username,
              onChange: (): void => {
                dispatch(
                  updateUser({
                    id: ma.id,
                    username: ma.username,
                    is_active: !ma.is_active,
                  })
                );
              },
            },
          },
        ],
      };
    });

  return (
    <>
      {machineAccountToken && (
        <MachineAccountsApiKeyModal
          machineAccountName={machineAccountName ?? ''}
          machineAccountToken={machineAccountToken}
        />
      )}
      <Table
        header='Machine Accounts'
        fields={fields}
        rows={rows}
        createAction={{
          onClick: (): void => {
            dispatch(createMachineAccount());
          },
          tooltip: 'Create Machine Account',
        }}
      />
    </>
  );
};

export default MachineAccounts;
